/**
 * The compliance calendar — FR-D3. A list since Wave 15C (C14 §10.6 items 1–2,
 * ADR-0033): the kit's table with search, filters and sort, and adding or
 * editing an item in a dialog.
 *
 * Two lists, as tabs, because a firm asks two different questions here:
 *
 * - **Per period, what is generated, due and overdue** — *Periods*. A row with
 *   a case opens it.
 * - **What recurs, and for how many clients** — *What recurs*. A row opens the
 *   item to edit, for a role that may.
 *
 * FR-D3 also asks for *completed* and *by staff*. Those are the case's status
 * and assignee; they are **not** on this screen and are not shown as zero,
 * because a zero that means "not implemented" is a number a firm plans against.
 *
 * The empty state is the screen. ADR-0010's last consequence says so directly:
 * *"a firm that defines nothing gets no generated cases and no warning beyond
 * an empty calendar screen that says so"*. So it says so, and it says why.
 *
 * **Skipping a period asks first** (ADR-0033 §1: anything that cancels asks) —
 * a skipped period is one no case will ever be opened for.
 */

import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  DropdownMenu,
  type DropdownMenuItem,
  EmptyState,
  Select,
  Tabs,
  buttonClass,
  formatDate,
} from '@fiveox/ui'
import { CalendarPlus, ChevronDown, Pencil, Play, RefreshCw } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import {
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  useListLoading,
  useListTable,
} from '../../components/page'
import { CALENDAR, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type CalendarItem,
  type Occurrence,
  type OccurrenceState,
  type Summary,
  type Vocabulary,
  appliesToLabel,
  calendar,
  dueLabel,
  frequencyLabel,
  leadLabel,
  runSummary,
  stateLabel,
} from '../../lib/api/calendar'
import { type Service, services as servicesApi } from '../../lib/api/services'
import { ItemDialog } from './ItemDialog'

const STATES: OccurrenceState[] = ['awaiting_case', 'overdue', 'opened', 'skipped']

function Stat({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
  return (
    <div className="border-border rounded-2xl border bg-card p-4 shadow-sm">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          alert && value > 0 ? 'text-destructive' : 'text-foreground'
        }`}
      >
        {value}
      </p>
    </div>
  )
}

function PeriodList({
  rows,
  periods,
  period,
  setPeriod,
  editable,
  creatable,
  running,
  onGenerate,
  onSkip,
}: {
  rows: Occurrence[]
  periods: Array<{ period: string; total: number }>
  period: string
  setPeriod: (value: string) => void
  editable: boolean
  creatable: boolean
  running: boolean
  onGenerate: () => void
  onSkip: (row: Occurrence) => void
}) {
  const loading = useListLoading()
  const navigate = useNavigate()
  const columns = useMemo<ListColumn<Occurrence>[]>(
    () => [
      {
        key: 'client',
        label: 'Client',
        width: 200,
        required: true,
        phone: true,
        sortField: 'client_name',
        // Truncated on a phone, or a long name pushes the State column —
        // the one a phone is shown the row for — under the scroll edge.
        render: (row) => (
          <span className="text-foreground block max-w-[6.5rem] truncate font-medium sm:max-w-none">{row.client_name}</span>
        ),
      },
      {
        key: 'item',
        label: 'Item',
        width: 160,
        sortField: 'item_name',
        render: (row) => <span className="text-muted-foreground">{row.item_name}</span>,
      },
      {
        key: 'period',
        label: 'Period',
        width: 100,
        sortField: 'period_end',
        render: (row) => <span className="tabular-nums">{row.period}</span>,
      },
      {
        key: 'due',
        label: 'Due',
        width: 110,
        sortField: 'due_date',
        render: (row) => <span className="whitespace-nowrap">{formatDate(row.due_date)}</span>,
      },
      {
        key: 'status',
        label: 'State',
        width: 180,
        phone: true,
        sortField: 'state',
        render: (row) => (
          <div className="min-w-0 space-y-0.5">
            <StatusBadge kind="calendar" status={row.state} />
            {row.case_id && (
              <Link
                to={`/cases/${row.case_id}`}
                onClick={(e) => e.stopPropagation()}
                className="text-primary block text-xs hover:underline"
              >
                Open the case
              </Link>
            )}
            {row.last_error && <p className="text-muted-foreground text-xs">{row.last_error}</p>}
          </div>
        ),
      },
    ],
    [],
  )

  const items = useMemo(() => [...new Set(rows.map((row) => row.item_name))].sort(), [rows])

  const list = useListTable(rows, {
    columns,
    search: (row) => [row.client_name, row.item_name, row.period],
    filters: [
      {
        key: 'state',
        label: 'State',
        allLabel: 'Any state',
        options: STATES.map((s) => ({ value: s, label: stateLabel(s) })),
        match: (row, value) => row.state === value,
      },
      {
        key: 'item',
        label: 'Item',
        allLabel: 'Every item',
        options: items.map((name) => ({ value: name, label: name })),
        match: (row, value) => row.item_name === value,
      },
    ],
    initialSort: { field: 'due_date', dir: 'asc' },
  })

  const periodPicker = (
    <Select
      size="sm"
      aria-label="Period"
      className="w-[calc(50vw-2rem)] sm:w-auto"
      value={period}
      onChange={(e) => setPeriod(e.target.value)}
    >
      <option value="">Every period</option>
      {periods.map((entry) => (
        <option key={entry.period} value={entry.period}>
          {entry.period} ({entry.total})
        </option>
      ))}
    </Select>
  )

  if (!loading && list.isEmpty && !period) {
    return (
      <EmptyState
        title="Nothing generated yet"
        description="Periods appear here once a case is due to open for them. Generate now to open what is due today rather than waiting for the nightly run."
        action={
          creatable ? (
            <Button onClick={onGenerate} loading={running}>
              <Play size={16} aria-hidden /> Generate now
            </Button>
          ) : undefined
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <ListToolbar list={list} placeholder="Search by client, item or period">
        {periodPicker}
      </ListToolbar>
      <ListTable
        list={list}
        rowKey={(row) => row.id}
        onRowClick={(row) => {
          if (row.case_id) navigate(`/cases/${row.case_id}`)
        }}
        trailing={
          editable
            ? {
                header: <span className="sr-only">Actions</span>,
                width: 90,
                cell: (row) =>
                  row.state !== 'opened' && row.state !== 'skipped' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSkip(row)
                      }}
                    >
                      Skip
                    </Button>
                  ) : null,
              }
            : undefined
        }
        empty={
          <span className="text-muted-foreground text-sm">Nothing was generated for {period}.</span>
        }
      />
    </div>
  )
}

function ItemList({
  items,
  editable,
  creatable,
  onEdit,
  onAdd,
}: {
  items: CalendarItem[]
  editable: boolean
  creatable: boolean
  onEdit: (item: CalendarItem) => void
  onAdd: () => void
}) {
  const loading = useListLoading()
  const columns = useMemo<ListColumn<CalendarItem>[]>(
    () => [
      {
        key: 'name',
        label: 'Item',
        width: 200,
        required: true,
        phone: true,
        sortField: 'name',
        render: (item) => (
          <span className="text-foreground inline-flex flex-wrap items-center gap-2 font-medium">
            {item.name}
            {!item.active && (
              <Badge size="sm" tone="neutral">
                Off
              </Badge>
            )}
          </span>
        ),
      },
      {
        key: 'service',
        label: 'Service',
        width: 160,
        sortField: 'service_name',
        render: (item) => <span className="text-muted-foreground">{item.service_name ?? '—'}</span>,
      },
      {
        key: 'rule',
        label: 'When',
        width: 260,
        render: (item) => (
          <span className="text-muted-foreground text-sm">
            {frequencyLabel(item.frequency, item.months)} · {dueLabel(item)}
            <span className="block text-xs">{leadLabel(item.lead_days)}</span>
          </span>
        ),
      },
      {
        key: 'applies',
        label: 'For',
        width: 180,
        render: (item) => (
          <span className="text-muted-foreground text-xs">
            {appliesToLabel(item.applies_to, item.tag)}
          </span>
        ),
      },
      {
        key: 'clients',
        label: 'Clients',
        width: 90,
        align: 'right',
        phone: true,
        sortField: 'matching_clients',
        render: (item) => <span className="tabular-nums">{item.matching_clients ?? 0}</span>,
      },
    ],
    [],
  )
  const serviceNames = useMemo(
    () => [...new Set(items.map((i) => i.service_name).filter((n): n is string => Boolean(n)))].sort(),
    [items],
  )
  const list = useListTable(items, {
    columns,
    search: (item) => [item.name, item.service_name, item.tag],
    filters: [
      {
        key: 'active',
        label: 'On or off',
        allLabel: 'On and off',
        options: [
          { value: 'on', label: 'On' },
          { value: 'off', label: 'Off' },
        ],
        match: (item, value) => (value === 'on' ? item.active : !item.active),
      },
      ...(serviceNames.length > 1
        ? [
            {
              key: 'service',
              label: 'Service',
              allLabel: 'Every service',
              options: serviceNames.map((n) => ({ value: n, label: n })),
              match: (item: CalendarItem, value: string) => item.service_name === value,
            },
          ]
        : []),
    ],
    initialSort: { field: 'name', dir: 'asc' },
  })

  if (!loading && list.isEmpty) {
    return (
      <EmptyState
        title="Nothing recurs yet"
        description="Add an item for each thing you file on a schedule, and the calendar opens its cases for you."
        action={
          creatable ? (
            <Button onClick={onAdd}>
              <CalendarPlus size={16} aria-hidden /> Add an item
            </Button>
          ) : undefined
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <ListToolbar list={list} placeholder="Search by item, service or tag" />
      <ListTable
        list={list}
        rowKey={(item) => item.id}
        onRowClick={editable ? onEdit : undefined}
        trailing={
          editable
            ? {
                header: <span className="sr-only">Actions</span>,
                width: 90,
                cell: (item) => (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation()
                      onEdit(item)
                    }}
                  >
                    <Pencil size={14} aria-hidden /> Edit
                  </Button>
                ),
              }
            : undefined
        }
      />
    </div>
  )
}

export function Calendar() {
  const { can } = useEvaluation()
  const editable = can(CALENDAR, 'can_update')
  const creatable = can(CALENDAR, 'can_create')

  const [items, setItems] = useState<CalendarItem[]>([])
  const [rows, setRows] = useState<Occurrence[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [periods, setPeriods] = useState<Array<{ period: string; total: number }>>([])
  const [period, setPeriod] = useState<string>('')
  const [services, setServices] = useState<Service[]>([])
  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null)
  const [editing, setEditing] = useState<CalendarItem | null | undefined>(undefined)
  const [skipping, setSkipping] = useState<Occurrence | null>(null)
  const [skipError, setSkipError] = useState<string | null>(null)
  // Unchosen, the tab is Periods — or What recurs while nothing is generated.
  const [chosenTab, setTab] = useState<'periods' | 'items' | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)

  const load = useCallback(async (forPeriod: string) => {
    const [itemsBody, occurrencesBody, periodsBody] = await Promise.all([
      calendar.items(),
      calendar.occurrences(forPeriod ? { period: forPeriod } : {}),
      calendar.periods(),
    ])
    setItems(itemsBody.data)
    setRows(occurrencesBody.data.occurrences)
    setSummary(occurrencesBody.data.summary)
    setPeriods(periodsBody.data)
    setLoading(false)
  }, [])

  useEffect(() => {
    void Promise.all([servicesApi.list(), calendar.vocabulary()])
      .then(([servicesBody, vocabularyBody]) => {
        setServices(servicesBody.data.services)
        setVocabulary(vocabularyBody.data)
      })
      .catch(() => setError('Your services could not be loaded.'))
  }, [])

  useEffect(() => {
    void load(period).catch(() => {
      setError('Your calendar could not be loaded.')
      setLoading(false)
    })
  }, [load, period])

  const run = async (kind: 'generate' | 'reconcile') => {
    setError(null)
    setNote(null)
    setRunning(true)
    try {
      const body = kind === 'generate' ? await calendar.generate() : await calendar.reconcile()
      setNote(runSummary(body.data))
      await load(period)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That run could not be started.')
    } finally {
      setRunning(false)
    }
  }

  const skip = async () => {
    if (!skipping) return
    setSkipError(null)
    try {
      await calendar.skip(skipping.id)
      setSkipping(null)
      await load(period)
    } catch (err) {
      setSkipError(err instanceof ApiError ? err.message : 'That period could not be skipped.')
    }
  }

  const tab = chosenTab ?? (rows.length === 0 && items.length > 0 ? 'items' : 'periods')

  const runs: DropdownMenuItem[] = creatable
    ? [
        {
          id: 'generate',
          label: 'Generate now',
          icon: <Play size={14} aria-hidden />,
          description: 'Open the cases due today, without waiting for the nightly run.',
          disabled: running,
          onSelect: () => void run('generate'),
        },
        {
          id: 'reconcile',
          label: 'Find what was missed',
          icon: <RefreshCw size={14} aria-hidden />,
          description: 'Look back for periods a run should have opened and did not.',
          disabled: running,
          onSelect: () => void run('reconcile'),
        },
      ]
    : []

  const primary: ReactNode = creatable ? (
    <>
      <DropdownMenu
        label="Run the calendar"
        items={runs}
        className="w-72"
        trigger={({ ref, open: _open, ...props }) => (
          <button ref={ref} type="button" className={buttonClass({ variant: 'outline' })} {...props}>
            {running ? 'Running…' : 'Run'} <ChevronDown size={14} aria-hidden />
          </button>
        )}
      />
      <Button onClick={() => setEditing(null)}>
        <CalendarPlus size={16} aria-hidden /> Add an item
      </Button>
    </>
  ) : undefined

  return (
    <>
      <ListPage
        title="Compliance calendar"
        description="What recurs for your clients, when it is due, and whether a case is open for it."
        primary={primary}
        loading={loading}
        error={error}
        isEmpty={items.length === 0 && rows.length === 0 && !period}
        empty={{
          title: 'Your calendar is empty',
          // ADR-0010's last consequence, said out loud. A firm arriving at an
          // empty compliance calendar in a product that has one must be told
          // that the emptiness is the design and not a failure to load.
          description:
            'It will stay that way until you define something. ProDesk ships no statutory due dates: professions differ, the law changes, and a built-in date that is wrong is a date you would have no reason to doubt. Add an item for each thing you file on a schedule and it will generate the cases.',
          action: creatable ? (
            <Button onClick={() => setEditing(null)}>
              <CalendarPlus size={16} aria-hidden /> Add your first item
            </Button>
          ) : undefined,
        }}
      >
        {note && <Alert tone="info">{note}</Alert>}

        {summary && rows.length > 0 && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Periods generated" value={summary.generated} />
            <Stat label="Case open" value={summary.opened} />
            <Stat label="Awaiting a case" value={summary.awaiting_case} />
            <Stat label="Overdue, no case" value={summary.overdue} alert />
          </div>
        )}

        <Tabs
          idBase="calendar"
          active={tab}
          onChange={(id) => setTab(id as 'periods' | 'items')}
          tabs={[
            { id: 'periods', label: 'Periods', badge: rows.length || undefined },
            { id: 'items', label: 'What recurs', badge: items.length || undefined },
          ]}
        />
        <div id={`calendar-${tab}-panel`} role="tabpanel" aria-labelledby={`calendar-${tab}`}>
          {tab === 'periods' ? (
            <PeriodList
              rows={rows}
              periods={periods}
              period={period}
              setPeriod={setPeriod}
              editable={editable}
              creatable={creatable}
              running={running}
              onGenerate={() => void run('generate')}
              onSkip={(row) => {
                setSkipError(null)
                setSkipping(row)
              }}
            />
          ) : (
            <ItemList
              items={items}
              editable={editable}
              creatable={creatable}
              onEdit={setEditing}
              onAdd={() => setEditing(null)}
            />
          )}
        </div>
      </ListPage>

      {editing !== undefined && (
        <ItemDialog
          item={editing}
          services={services}
          vocabulary={vocabulary}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined)
            void load(period)
          }}
        />
      )}

      <ConfirmDialog
        open={skipping !== null}
        onClose={() => setSkipping(null)}
        title="Skip this period?"
        confirmLabel="Skip it"
        cancelLabel="Keep it"
        error={skipError}
        onConfirm={() => void skip()}
      >
        {skipping && (
          <p>
            No case will be opened for <strong>{skipping.item_name}</strong>,{' '}
            <strong>{skipping.period}</strong>, for <strong>{skipping.client_name}</strong>.
          </p>
        )}
      </ConfirmDialog>
    </>
  )
}
